import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { appConstants } from "../_constants/app.constants";
import { getImmersiveMatch, immersiveFallbackPath } from "./immersiveRoutes";
import { Button, Modal, IconArrowLeft, IconClose, IconMenu, IconStar } from "../ui";

function useIsMobile() {
  const getMatch = () =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false;
  const [isMobile, setIsMobile] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

export default function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, ready, logout } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [myTournamentCount, setMyTournamentCount] = useState(null);

  const { isImmersive, tournamentId: immersiveTournamentId, immersiveMode } = useMemo(
    () => getImmersiveMatch(location.pathname),
    [location.pathname]
  );

  const showImmersiveBackFab = isImmersive && immersiveMode !== "posterStudio";

  const immersiveBackTo = useMemo(
    () => immersiveFallbackPath(immersiveTournamentId),
    [immersiveTournamentId]
  );

  useEffect(() => {
    if (!ready || !user) {
      setMyTournamentCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${appConstants.API_URL}/api/tournaments`);
        const n = Array.isArray(res.data) ? res.data.length : 0;
        if (!cancelled) setMyTournamentCount(n);
      } catch {
        if (!cancelled) setMyTournamentCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, user, location.pathname]);

  const showMisTorneos = Boolean(user && myTournamentCount !== null && myTournamentCount > 0);

  const authLinks = useMemo(() => {
    if (!user) return [];
    const items = [{ to: "/documentacion", label: "Documentacion" }];
    if (showMisTorneos) {
      items.push({ to: "/mis-torneos", label: "Mis torneos" });
    }
    items.push({ to: "/mis-favoritos", label: "Mis favoritos", icon: "star" });
    items.push({ to: "/crear-torneo", label: "Crear torneo" });
    return items;
  }, [user, showMisTorneos]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const closeDrawer = () => setDrawerOpen(false);

  const linkClass = (path) =>
    [
      "rounded-lg px-3 py-2 text-sm font-semibold tracking-wide transition-colors",
      isActive(path)
        ? "border border-gold-bright/40 bg-white/10 text-gold-bright"
        : "text-white/85 hover:bg-white/10 hover:text-white",
    ].join(" ");

  const renderNavLink = (item) => (
    <RouterLink key={item.to} to={item.to} className={linkClass(item.to)}>
      <span className="inline-flex items-center gap-1.5">
        {item.icon === "star" ? <IconStar size={15} className="opacity-90" /> : null}
        {item.label}
      </span>
    </RouterLink>
  );

  return (
    <div className="min-h-screen flex flex-col bg-cream font-sans text-ink">
      {showImmersiveBackFab ? (
        <div className="fixed left-3 top-3 z-[5000]">
          <Button
            as={RouterLink}
            to={immersiveBackTo}
            variant="primary"
            size="md"
            className="rounded-full bg-arena/95 px-4 backdrop-blur-md"
          >
            <IconArrowLeft size={16} />
            Volver
          </Button>
        </div>
      ) : null}

      {!isImmersive ? (
        <header className="sticky top-0 z-50 border-b border-white/10 shadow-card">
          <div className="absolute inset-0 bg-mesh-hero opacity-95" aria-hidden />
          <div
            className="absolute inset-0 bg-noise opacity-60 mix-blend-overlay pointer-events-none"
            aria-hidden
          />
          <div className="relative mx-auto flex min-h-16 max-w-[1600px] min-w-0 items-center gap-1.5 px-2 sm:gap-2 sm:px-5 lg:px-8">
            {isMobile && user && ready ? (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="focus-ring shrink-0 rounded-lg p-2 text-white"
                aria-label="Abrir menú"
              >
                <IconMenu />
              </button>
            ) : null}

            <RouterLink
              to="/"
              className={`min-w-0 font-display font-extrabold tracking-tight text-white hover:text-gold-bright ${
                isMobile ? "flex-1 truncate text-lg" : "text-xl"
              }`}
            >
              Club Evolver
            </RouterLink>

            {!isMobile && ready && user ? (
              <nav className="ml-6 flex min-w-0 flex-1 flex-wrap items-center gap-1 lg:gap-2">
                {authLinks.map(renderNavLink)}
              </nav>
            ) : null}

            {!isMobile && !ready ? (
              <div className="ml-auto flex flex-1 items-center justify-end gap-2 pr-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-gold-bright/80" aria-hidden />
                <span className="text-xs font-medium text-white/60">Cargando...</span>
              </div>
            ) : null}

            <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
              {ready && !user && !isMobile ? (
                <>
                  <RouterLink
                    to="/login"
                    className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-gold-bright via-amber-200 to-gold px-5 py-2.5 text-sm font-extrabold text-ink shadow-lg shadow-amber-900/20 ring-2 ring-white/25 transition duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-gold-bright/30 active:scale-[0.98]"
                  >
                    <span
                      className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition duration-500 group-hover:translate-x-full"
                      aria-hidden
                    />
                    <span className="relative">Entrar para gestionar tu torneo</span>
                  </RouterLink>
                  <RouterLink
                    to="/register"
                    className="text-sm font-semibold text-white/80 underline-offset-4 transition hover:text-white hover:underline"
                  >
                    Crear cuenta gratis
                  </RouterLink>
                </>
              ) : null}
              {ready && user ? (
                <>
                  <RouterLink
                    to="/crear-torneo"
                    className="hidden rounded-lg bg-btn-gold px-3 py-1.5 text-xs font-bold text-ink shadow-md hover:brightness-105 sm:inline-flex"
                  >
                    + Torneo
                  </RouterLink>
                  <span className="hidden max-w-[280px] truncate text-xs text-white/80 md:inline-block" title={user.email}>
                    {user.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => logout()}
                    className="focus-ring rounded-lg border border-white/40 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-white/10 sm:px-3 sm:text-xs"
                  >
                    Salir
                  </button>
                </>
              ) : null}
              {ready && !user && isMobile ? (
                <RouterLink
                  to="/login"
                  className="inline-flex max-w-[min(100%,240px)] items-center justify-center rounded-full bg-gradient-to-r from-gold-bright via-amber-200 to-gold px-3 py-2 text-[11px] font-extrabold leading-tight text-ink shadow-md ring-1 ring-white/30"
                >
                  Entrar / Gestionar
                </RouterLink>
              ) : null}
            </div>
          </div>
        </header>
      ) : null}

      <Modal open={drawerOpen && !isImmersive && Boolean(user)} onClose={closeDrawer} ariaLabel="Menu principal">
        <div className="h-full w-[280px] bg-arena text-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <p className="font-display text-lg font-bold">Menú</p>
            <button type="button" onClick={closeDrawer} className="focus-ring rounded-lg p-2" aria-label="Cerrar menú">
              <IconClose />
            </button>
          </div>
          <nav className="flex flex-col p-2">
            {authLinks.map((item) => (
              <RouterLink
                key={item.to}
                to={item.to}
                onClick={closeDrawer}
                className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive(item.to) ? "bg-white/20 text-gold-bright" : "text-white hover:bg-white/10"
                }`}
              >
                {item.icon === "star" ? <IconStar size={16} /> : null}
                {item.label}
              </RouterLink>
            ))}
            <button
              type="button"
              onClick={() => {
                closeDrawer();
                logout();
              }}
              className="focus-ring mt-3 rounded-xl px-3 py-2 text-left text-sm font-semibold text-gold-bright hover:bg-white/10"
            >
              Salir
            </button>
          </nav>
        </div>
      </Modal>

      <main className={["flex-1 w-full flex flex-col", isImmersive ? "min-h-screen" : "min-h-[calc(100vh-4rem)]"].join(" ")}>
        <Outlet />
      </main>

      {!isImmersive ? (
        <footer className="mt-auto border-t border-arena/20 bg-gradient-to-b from-arena to-ink px-4 py-8">
          <div className="mx-auto max-w-6xl text-center text-sm leading-relaxed text-white/55">
            <p>Modo visor a pantalla completa para TVs y videowalls, ideal para que el público siga el torneo en sala.</p>
          </div>
        </footer>
      ) : null}
    </div>
  );
}
